import { useState } from 'react';

import type {
  NewReport,
  ReportAccount,
  ReportOptions,
  ReportSource,
} from '../../api/monitor';
import { SearchSelect } from '../../components/ui/SearchSelect';
import { accountLabel, masterLabel } from '../../lib/reports';

interface ReportFormProps {
  options: ReportOptions;
  accounts: ReportAccount[];
  masterId: number | null;
  creating: boolean;
  onSelectMaster: (masterId: number | null) => void;
  onSubmit: (payload: NewReport) => Promise<void>;
  /** E-mail de quem está logado, gravado junto com o pedido. */
  requestedBy: string | null;
}

const FIELD =
  'border-line-300 focus:border-navy-800 rounded-[10px] border bg-white px-3 py-[11px] text-[14px] text-ink-900 outline-none focus:ring-2 focus:ring-[#00244733]';

const LABEL = 'flex flex-col gap-[5px] text-[12px] font-bold text-ink-600';

/**
 * Filtros do relatório.
 *
 * Master e período são obrigatórios porque sem eles o recorte seria a base
 * inteira, e a coleta bate na operadora uma vez por linha. Conta e linha
 * estreitam — a conta só abre depois do master, já que a lista de contas
 * depende dele.
 */
export function ReportForm({
  options,
  accounts,
  masterId,
  creating,
  onSelectMaster,
  onSubmit,
  requestedBy,
}: ReportFormProps) {
  const [accountId, setAccountId] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [line, setLine] = useState('');
  const [source, setSource] = useState<ReportSource>('api');
  const [error, setError] = useState<string | null>(null);

  const master = options.masters.find((candidate) => candidate.id === masterId) ?? null;
  const chosenPeriod = options.periods.find((candidate) => candidate.start === period) ?? null;

  const chosenSource =
    options.sources.find((candidate) => candidate.id === source) ?? options.sources[0] ?? null;

  // O portal da Vivo entrega a conta inteira nos ciclos que ele decide: não há
  // período a escolher nem linha a filtrar antes do download. Desabilitar é
  // mais honesto do que aceitar um filtro que a origem não sabe cumprir.
  const fromPortal = source === 'vivo_portal_last3';

  /** As contas oferecidas: no modo portal, só as da operadora daquela origem. */
  const visibleAccounts = chosenSource?.operator
    ? accounts.filter((account) => account.operator.code === chosenSource.operator)
    : accounts;

  /** Uma origem restrita a uma operadora só serve a master que tem conta nela. */
  const sourceAvailable = (operator: string | null) =>
    !operator || !master || master.operators.includes(operator);

  const handleMaster = (value: string) => {
    onSelectMaster(value ? Number(value) : null);
    // A conta escolhida pertencia ao master anterior; mantê-la mandaria ao
    // servidor um par master/conta que ele recusa.
    setAccountId('');
  };

  const handleSource = (value: string) => {
    setSource(value as ReportSource);
    // A conta pode ser de outra operadora que a origem nova não atende; deixá-la
    // selecionada mandaria ao servidor um par que ele recusa.
    setAccountId('');
  };

  const handleSubmit = async () => {
    if (!masterId) {
      setError('Escolha o master do relatório.');
      return;
    }

    if (!fromPortal && !period) {
      setError('Preencha os campos obrigatórios: master e período de fatura.');
      return;
    }

    setError(null);

    // No modo portal o período é do arquivo. Manda-se o ciclo corrente só para
    // o pedido nascer válido; o servidor o substitui pelos ciclos que vierem.
    const chosen = fromPortal
      ? options.periods[0]
      : options.periods.find((candidate) => candidate.start === period);

    if (!chosen) {
      setError('Período de fatura inválido.');
      return;
    }

    try {
      await onSubmit({
        masterId,
        accountId: accountId ? Number(accountId) : null,
        line: fromPortal ? '' : line.trim(),
        periodStart: chosen.start,
        periodEnd: chosen.end,
        requestedBy,
        source,
      });

      setLine('');
    } catch (cause) {
      setError((cause as Error).message);
    }
  };

  return (
    <div className="border-line-200 rounded-[14px] border bg-white p-[18px]">
      <div className="mb-0.5 text-[15px] font-extrabold">Relatório de consumo de linhas</div>
      <div className="text-ink-600 mb-3.5 text-[12.5px]">
        {fromPortal
          ? 'Master obrigatório. O período vem do próprio arquivo do portal — são os últimos ciclos que a Vivo tiver da conta.'
          : 'Master e período de fatura são obrigatórios. Informe uma linha para restringir o relatório.'}
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
        <label className={LABEL}>
          Master *
          <SearchSelect
            value={masterId === null ? '' : String(masterId)}
            onChange={handleMaster}
            options={options.masters.map((candidate) => ({
              value: String(candidate.id),
              label: masterLabel(candidate),
            }))}
            clearable
            placeholder="Selecione o master…"
            searchPlaceholder="Buscar master…"
          />
        </label>

        <label className={LABEL}>
          Origem dos dados *
          <SearchSelect
            value={source}
            onChange={handleSource}
            options={options.sources.map((candidate) => ({
              value: candidate.id,
              label: candidate.name,
              // Um master sem conta da operadora da origem não tem o que
              // baixar; o servidor recusaria, e recusar aqui poupa a ida.
              disabled: !sourceAvailable(candidate.operator),
              hint: sourceAvailable(candidate.operator)
                ? undefined
                : 'master sem conta nesta operadora',
            }))}
            searchPlaceholder="Buscar origem…"
          />
        </label>

        <label className={LABEL}>
          Conta (opcional)
          <SearchSelect
            value={accountId}
            onChange={setAccountId}
            options={visibleAccounts.map((account) => ({
              value: String(account.id),
              label: accountLabel(account),
            }))}
            disabled={!masterId}
            clearable
            placeholder={masterId ? 'Todas as contas do master' : 'Selecione o master primeiro'}
            searchPlaceholder="Buscar conta…"
          />
        </label>

        <label className={LABEL}>
          {fromPortal ? 'Período de fatura' : 'Período de fatura *'}
          <SearchSelect
            value={fromPortal ? '' : period}
            onChange={setPeriod}
            options={options.periods.map((candidate) => ({
              value: candidate.start,
              label: candidate.label,
              hint: candidate.current ? 'ciclo atual' : undefined,
            }))}
            disabled={fromPortal}
            clearable
            placeholder={fromPortal ? 'Definido pelo arquivo do portal' : 'Selecione o período…'}
            searchPlaceholder="Buscar período…"
          />
        </label>

        <label className={LABEL}>
          Linha (opcional)
          <input
            value={fromPortal ? '' : line}
            onChange={(event) => setLine(event.target.value)}
            placeholder={fromPortal ? 'Todas as linhas da conta' : '(62) 9xxxx-xxxx'}
            inputMode="tel"
            disabled={fromPortal}
            className={`${FIELD} ${fromPortal ? 'text-ink-400 cursor-not-allowed bg-[#F7F9FC]' : ''}`}
          />
        </label>
      </div>

      {error ? (
        <div className="text-danger-600 mt-2.5 text-[12.5px] font-bold">{error}</div>
      ) : null}

      {/* Vivo e Claro só respondem o acumulado do ciclo corrente. Escolher um
          ciclo fechado recorta o relatório, mas não volta no tempo — e ler
          "10/06 – 09/07" como histórico seria o engano fácil de cometer. */}
      {!fromPortal && chosenPeriod && !chosenPeriod.current ? (
        <div className="border-warn-200 bg-warn-100 text-warn-700 mt-2.5 rounded-[10px] border px-3 py-2.5 text-[11.5px] font-semibold">
          As operadoras só informam o consumo do ciclo em andamento. O relatório sai marcado
          com o período escolhido, mas os valores são a leitura de agora.
        </div>
      ) : null}

      {/* A origem muda o que o relatório é, não só como ele é feito: um traz o
          ciclo corrente linha a linha, o outro traz ciclos fechados do portal.
          A descrição vem do servidor para não haver duas versões do texto. */}
      {chosenSource ? (
        <div className="text-ink-600 mt-2.5 text-[11.5px]">{chosenSource.description}</div>
      ) : null}

      {/* O tempo de espera é a informação que falta para a pessoa decidir se
          pede o master inteiro ou estreita por conta antes de clicar. */}
      {master ? (
        <div className="text-ink-400 mt-2.5 text-[11.5px]">
          {fromPortal ? (
            <>
              {visibleAccounts.length || master.accounts} conta(s) da Vivo no escopo atual. O
              portal gera um arquivo por conta, então a espera cresce com o número de contas —
              não com o de linhas.
            </>
          ) : (
            <>
              {master.lines} linha{master.lines === 1 ? '' : 's'} no escopo atual. A coleta
              consulta a operadora uma vez por linha, então relatórios grandes levam alguns
              minutos.
            </>
          )}
        </div>
      ) : null}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={creating}
        className="bg-navy-800 hover:bg-navy-950 mt-3.5 rounded-[10px] px-[22px] py-3 text-[14px] font-extrabold text-white disabled:opacity-60"
      >
        {creating ? 'Enviando…' : 'Gerar relatório'}
      </button>
    </div>
  );
}
